import { PayableInvoicesToReviewBadge } from "@/components/shared/app-navigation/sidebar/badges/company";
import { ActionCard } from "@/components/shared/cards/action-card";
import AIProcessingCard from "@/components/shared/cards/ai-processing-card";
import { NoRecordsEmpty } from "@/components/shared/data-table/empty-message";
import { DocumentUploadDropzone } from "@/components/shared/dropzones/document-upload-dropzone";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import UploadProgressBox from "@/features/upload-progress-box/UploadProgressBox";
import useReconEnabled from "@/hooks/use-recon-enabled";
import {
  InvoiceSmbReviewStatus,
  InvoiceType,
} from "@/lib/constants/invoiceStatus";
import { useInvoiceBrowser } from "@/providers/invoice-browser";
import { useTenant } from "@/providers/session";
import { useDeleteInvoice } from "@/queries/invoices/delete";
import { useUpdateInvoices } from "@/queries/invoices/update";
import { useLocalStorage } from "@uidotdev/usehooks";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { UploadInvoicesDrawer } from "../../../components/shared/drawers/upload-invoices-drawer";
import {
  getColumnsForAll,
  getColumnsForReviewed,
  getColumnsForToReview,
} from "./columns";
import { InvoicesDataTable, InvoicesTab } from "./invoices-data-table";

export default function Invoices() {
  const deleteInvoices = useDeleteInvoice();
  const [updateInvoices] = useUpdateInvoices();
  const { tenantId, hasCostCenters } = useTenant();
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();
  const { setInvoiceBrowsingQueue } = useInvoiceBrowser();

  const [tab, setTab] = useLocalStorage("invoices-tab", InvoicesTab.TO_REVIEW);

  useEffect(() => {
    setInvoiceBrowsingQueue([]);
  }, [tab]);

  const { reconEnabled } = useReconEnabled();

  const toReviewColumns = useMemo(
    () =>
      getColumnsForToReview({
        hasCostCenters: hasCostCenters ?? false,
        reconEnabled,
      }),
    [hasCostCenters, reconEnabled],
  );
  const reviewedColumns = useMemo(
    () =>
      getColumnsForReviewed({
        hasCostCenters: hasCostCenters ?? false,
        reconEnabled,
      }),
    [hasCostCenters, reconEnabled],
  );
  const allColumns = useMemo(
    () =>
      getColumnsForAll({
        hasCostCenters: hasCostCenters ?? false,
        reconEnabled,
      }),
    [hasCostCenters, reconEnabled],
  );

  return (
    <>
      <DocumentUploadDropzone tenantId={tenantId}>
        <div className="flex flex-col h-full gap-4">
          <div className="flex items-center mb-2 gap-4">
            <ActionCard
              title={t("payables.upload_sheet_title")}
              description={t("payables.upload_sheet_description")}
              onClick={() => setIsOpen(true)}
            />
            <AIProcessingCard screen="invoices" />
          </div>
          <Tabs
            value={tab}
            className="flex flex-1 flex-col w-full h-full"
            onValueChange={(value) => setTab(value as InvoicesTab)}
          >
            <TabsList className="pb-4">
              <TabsTrigger
                value={InvoicesTab.TO_REVIEW}
                className="flex items-center py-3 px-6 gap-4 font-medium text-lg"
              >
                {t("payment_states.to_review")}
                <PayableInvoicesToReviewBadge />
              </TabsTrigger>
              <TabsTrigger
                value={InvoicesTab.REVIEWED}
                className="flex items-center py-3 px-6 gap-2 font-medium text-lg"
              >
                {t("payment_states.reviewed")}
              </TabsTrigger>
              <TabsTrigger
                value={InvoicesTab.ALL}
                className="flex items-center py-3 px-6 gap-2 font-medium text-lg"
              >
                {t("all_invoices")}
              </TabsTrigger>
            </TabsList>
            <TabsContent value={InvoicesTab.TO_REVIEW} className="flex-1">
              <InvoicesDataTable
                storageKey="invoices-to-review"
                defaultFilters={{
                  type: [InvoiceType.PAYABLE],
                  smbReviewStatus: [InvoiceSmbReviewStatus.UNREVIEWED],
                }}
                columns={toReviewColumns}
                onDelete={handleDelete}
                onConfirm={handleConfirm}
                onPaid={handlePaid}
                tab={InvoicesTab.TO_REVIEW}
                emptyStateComponent={<NoRecordsEmpty state="done" />}
                guidingMessage={t("invoices_to_review_guiding_message")}
              />
            </TabsContent>
            <TabsContent value={InvoicesTab.REVIEWED} className="flex-1">
              <InvoicesDataTable
                storageKey="invoices-reviewed"
                defaultFilters={{
                  type: [InvoiceType.PAYABLE],
                  smbReviewStatus: [
                    InvoiceSmbReviewStatus.REVIEWED,
                    InvoiceSmbReviewStatus.REVIEWED_ZERO_TOUCH,
                  ],
                }}
                columns={reviewedColumns}
                onDelete={handleDelete}
                onConfirm={handleConfirm}
                onPaid={handlePaid}
                tab={InvoicesTab.REVIEWED}
                guidingMessage={t("invoices_reviewed_guiding_message")}
                emptyStateComponent={<NoRecordsEmpty state="done" />}
              />
            </TabsContent>
            <TabsContent value={InvoicesTab.ALL} className="flex-1">
              <InvoicesDataTable
                storageKey="invoices-all"
                defaultFilters={{
                  type: [InvoiceType.PAYABLE],
                }}
                columns={allColumns}
                onDelete={handleDelete}
                onConfirm={handleConfirm}
                onPaid={handlePaid}
                tab={InvoicesTab.ALL}
                guidingMessage={t("invoices_all_guiding_message")}
              />
            </TabsContent>
          </Tabs>
        </div>
      </DocumentUploadDropzone>
      <UploadProgressBox />
      <UploadInvoicesDrawer isOpen={isOpen} setIsOpen={setIsOpen} />
    </>
  );

  function handleDelete(invoiceIds: string[]) {
    deleteInvoices(invoiceIds);
  }

  function handleConfirm(invoiceIds: string[]) {
    updateInvoices(invoiceIds, {
      smbReviewStatus: InvoiceSmbReviewStatus.REVIEWED,
    });
  }

  function handlePaid(invoiceIds: string[]) {
    updateInvoices(invoiceIds, {
      isPaid: true,
    });
  }
}
